import { useEffect, useRef, useState } from 'preact/hooks';
import { texts, useLang } from './i18n.js';
import { Icon } from './shell.js';
import { Avatar, toast } from './ui.js';

const TX = texts(
  {
    searchPerson: 'Search name…',
    noOneMatches: 'No one matches',
    pickHint: 'Click to add, click again to remove',
    freelance: 'freelance',
    primaryLower: 'primary',
    nobody: 'There is nobody to pick from yet.'
  },
  {
    searchPerson: 'Sök namn…',
    noOneMatches: 'Ingen matchar',
    pickHint: 'Klicka för att lägga till, igen för att ta bort',
    freelance: 'frilans',
    primaryLower: 'huvudansvarig',
    nobody: 'Det finns ingen att välja än.'
  }
);

/**
 * The empty spot for a person (owner 2026-11-01: "the dashed ball shall be everywhere you can pick a role"): a dashed
 * circle with a plus, the same size as the initial balls around it. Wrap it in a button; it carries no meaning itself.
 */
export function AddBall(p: { size?: 'sm' | 'md' }) {
  return (
    <span class={`add-ball${p.size === 'sm' ? ' add-ball--sm' : ''}`} aria-hidden="true">
      <Icon name="plus" />
    </span>
  );
}

export interface PickPerson {
  id: string;
  name: string;
  freelancer?: boolean;
}

/**
 * Picking people for a role: a searchable drop-down by the button that opened it (owner 2026-10-31: "a simple list where
 * you can search for the right person"). With `multi` a click adds or removes a person, otherwise it picks one (and
 * clicking the picked one clears it). `onPick` saves; the list closes when it resolves and stays open on an error.
 */
export function PersonPicker(p: {
  label: string;
  people: PickPerson[] | null;
  current: string[];
  multi?: boolean;
  at: HTMLElement;
  empty?: string;
  onPick: (ids: string[]) => Promise<unknown>;
  onClose: () => void;
}) {
  const t = TX[useLang()];
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const multi = p.multi ?? true;
  const r = p.at.getBoundingClientRect();
  const width = Math.min(300, innerWidth - 16);
  const below = innerHeight - r.bottom > 300 || r.top < 300;
  const pos = { left: `${Math.max(8, Math.min(r.left, innerWidth - width - 8))}px`, width: `${width}px`, ...(below ? { top: `${r.bottom + 4}px` } : { bottom: `${innerHeight - r.top + 4}px` }) };
  useEffect(() => {
    input.current?.focus();
    const out = (e: Event) => {
      const el = e.target as Node;
      if (!box.current?.contains(el) && !p.at.contains(el)) p.onClose();
    };
    const gone = () => p.onClose();
    document.addEventListener('mousedown', out);
    addEventListener('resize', gone);
    addEventListener('scroll', gone, true);
    return () => {
      document.removeEventListener('mousedown', out);
      removeEventListener('resize', gone);
      removeEventListener('scroll', gone, true);
    };
  }, []);
  const all = p.people ?? [];
  const needle = q.trim().toLowerCase();
  // the ones already picked first, then everyone else by name
  const list = all.filter((x) => !needle || x.name.toLowerCase().includes(needle)).sort((a, b) => Number(p.current.includes(b.id)) - Number(p.current.includes(a.id)) || a.name.localeCompare(b.name, 'sv'));
  const pick = async (id: string) => {
    if (busy) return;
    setBusy(true);
    const on = p.current.includes(id);
    const ids = multi ? (on ? p.current.filter((x) => x !== id) : [...p.current, id]) : on ? [] : [id];
    try {
      await p.onPick(ids);
      p.onClose();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
      setBusy(false);
    }
  };
  const key = (e: KeyboardEvent) => {
    const k = e.key;
    if (k !== 'Escape' && k !== 'ArrowDown' && k !== 'ArrowUp' && k !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation(); // Escape in a list opened from a dialog closes the list, not the dialog
    if (k === 'Escape') {
      p.onClose();
      p.at.focus();
    } else if (k === 'ArrowDown') setHi(Math.min(hi + 1, list.length - 1));
    else if (k === 'ArrowUp') setHi(Math.max(hi - 1, 0));
    else if (list[hi]) void pick(list[hi]!.id);
  };
  return (
    <div class="pp" ref={box} style={pos} role="dialog" aria-label={p.label} onClick={(e) => e.stopPropagation()}>
      <input
        class="input pp-q"
        type="search"
        placeholder={t.searchPerson}
        aria-label={`${p.label}: ${t.searchPerson}`}
        aria-controls="pp-list"
        value={q}
        ref={input}
        onInput={(e) => (setQ((e.target as HTMLInputElement).value), setHi(0))}
        onKeyDown={key}
      />
      {!p.people ? (
        <p class="small muted pp-empty">…</p>
      ) : !all.length ? (
        <p class="small muted pp-empty">{p.empty ?? t.nobody}</p>
      ) : !list.length ? (
        <p class="small muted pp-empty">{t.noOneMatches}</p>
      ) : (
        <ul class="pp-list" id="pp-list" role="listbox" aria-multiselectable={multi}>
          {list.map((x, i) => {
            const on = p.current.includes(x.id);
            return (
              <li
                role="option"
                aria-selected={on}
                class={`pp-opt${i === hi ? ' is-hi' : ''}${on ? ' is-on' : ''}`}
                onMouseEnter={() => setHi(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void pick(x.id)}
              >
                <Avatar name={x.name} size="sm" decorative />
                <span class="pp-name">{x.name}</span>
                {x.freelancer ? <span class="hint">{t.freelance}</span> : null}
                {multi && p.current[0] === x.id && p.current.length > 1 ? <span class="hint">{t.primaryLower}</span> : null}
                <span class="pp-check" aria-hidden="true">{on ? '✓' : ''}</span>
              </li>
            );
          })}
        </ul>
      )}
      {multi && p.current.length ? <p class="small muted pp-foot">{t.pickHint}</p> : null}
    </div>
  );
}
